import api from './api';
import type {
  Broker,
  BrokerEmployee,
  BrokerUser,
  BrokerCreateRequest,
  BrokerFilters,
  POSP,
  POSPCreateRequest,
  POSPCreateLoginResponse,
  POSPFilters,
  POSPCompliance,
  POSPComplianceUpdate,
  POSPComplianceUpdateResponse,
  POSPPolicy,
  POSPEarningsSummary,
  POSPTarget,
  POSPPayout,
  PaginatedResponse,
} from '@/types';

/** Narrows GET /brokers/posps/{id}/policies/ on the policy's effective date
 *  (issue_date, falling back to inception_date). The endpoint honours ?period
 *  (all/monthly/fy) and explicit ?date_from/?date_to — the same windows as the
 *  by-insurer drill-down — but NOT ?insurer, so that key is omitted here. */
export interface POSPPolicyFilters {
  period?: string;
  date_from?: string;
  date_to?: string;
}

/** POSP-1 — the agent's own bank account, in full.
 *
 *  Served ONLY by GET/PATCH /brokers/posps/{id}/bank-details/, and only to the
 *  agent themselves: a Broker Admin, a super-POSP reading a sub-POSP, a country
 *  admin and a super_admin are all refused there. Everywhere else in the app the
 *  account arrives as `bank_account_number_masked` and nothing else, so this
 *  type must never be used to render a screen a broker can open.
 */
export interface POSPBankDetails {
  bank_name: string;
  bank_account_number: string;
  bank_ifsc_code: string;
  /** What every other screen shows. Echoed back after a save so the form can
   *  repaint from what was STORED rather than from what it submitted. */
  bank_account_number_masked: string;
}

export type POSPBankDetailsUpdate = Partial<
  Pick<POSPBankDetails, 'bank_name' | 'bank_account_number' | 'bank_ifsc_code'>
>;

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export const brokerService = {
  async getBrokers(page: number = 1, filters?: BrokerFilters): Promise<PaginatedResponse<Broker>> {
    const params = new URLSearchParams();
    params.append('page', page.toString());

    if (filters?.search) params.append('search', filters.search);
    if (filters?.status) params.append('status', filters.status);
    // U-04. This allowlist is why `ordering` had to be added here as well as to
    // `BrokerFilters`: a key the list does not name is dropped on the floor, so
    // a sort header would have rendered, fired, and sent nothing at all.
    if (filters?.ordering) params.append('ordering', filters.ordering);

    const response = await api.get<PaginatedResponse<Broker>>(`/brokers/?${params.toString()}`);
    return response.data;
  },

  async getBroker(id: string): Promise<Broker> {
    const response = await api.get<Broker>(`/brokers/${id}/`);
    return response.data;
  },

  /** For broker-side callers only (broker_admin / broker_employee): GET
   *  /brokers/{id}/ 403s for them (FIX 28 keeps `retrieve` closed to broker
   *  roles), so there is no way for them to read their own Broker row via
   *  getBroker. This resolves the CALLER's own broker instead -- see
   *  BrokerViewSet.me in the backend. Country/super admins keep using
   *  getBroker. */
  async getMyBroker(): Promise<Broker> {
    const response = await api.get<Broker>('/brokers/me/');
    return response.data;
  },

  async createBroker(data: BrokerCreateRequest): Promise<Broker> {
    const response = await api.post<Broker>('/brokers/', data);
    return response.data;
  },

  async updateBroker(id: string, data: Partial<BrokerCreateRequest>): Promise<Broker> {
    const response = await api.patch<Broker>(`/brokers/${id}/`, data);
    return response.data;
  },

  /** Activate, suspend or block a broker organisation.
   *
   *  The ONLY way the status column moves: it is read-only on the ordinary
   *  broker PATCH precisely so a suspended broker's own admin cannot patch
   *  themselves back to active. Country-admin gated and audited server-side.
   *
   *  Blocking or suspending also bars the whole team from logging in, on every
   *  login route -- so this is not a label, it is the switch.
   */
  async setBrokerStatus(
    id: string,
    status: 'active' | 'suspended' | 'blocked',
    reason?: string,
  ): Promise<{ status: string; previous_status: string; reason: string }> {
    const response = await api.post<{ status: string; previous_status: string; reason: string }>(
      `/brokers/${id}/set-status/`,
      { status, reason: reason ?? '' },
    );
    return response.data;
  },

  async getBrokerEmployees(brokerId: string): Promise<BrokerEmployee[]> {
    const response = await api.get<PaginatedResponse<BrokerEmployee>>(`/brokers/employees/`, {
      params: { broker: brokerId },
    });
    return response.data.results;
  },

  async getBrokerUsers(brokerId: string): Promise<BrokerUser[]> {
    const response = await api.get<BrokerUser[]>(`/brokers/${brokerId}/users/`);
    return response.data;
  },

  async mapUserToBroker(brokerId: string, userId: string, role: string, isPrimary: boolean = false): Promise<{ message: string; broker_user_id: string; created: boolean }> {
    const response = await api.post(`/brokers/${brokerId}/map_user/`, {
      user_id: userId,
      role,
      is_primary: isPrimary,
    });
    return response.data;
  },

  async getPOSPs(page: number = 1, filters?: POSPFilters): Promise<PaginatedResponse<POSP>> {
    const params = new URLSearchParams();
    params.append('page', page.toString());

    if (filters?.search) params.append('search', filters.search);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.broker) params.append('broker', filters.broker);
    if (filters?.is_active !== undefined) params.append('is_active', String(filters.is_active));
    // U-04 -- see getBrokers above: an unlisted key never reaches the wire.
    if (filters?.ordering) params.append('ordering', filters.ordering);
    if (filters?.page_size) params.append('page_size', String(filters.page_size));

    const response = await api.get<PaginatedResponse<POSP>>(`/brokers/posps/?${params.toString()}`);
    return response.data;
  },

  async getPOSP(id: string): Promise<POSP> {
    const response = await api.get<POSP>(`/brokers/posps/${id}/`);
    return response.data;
  },

  /** POSP-6: the logged-in POSP's own profile, so My Portal can open straight
   *  onto it instead of a list the POSP has to click through. */
  async getMyPOSP(): Promise<POSP> {
    const response = await api.get<POSP>('/brokers/posps/me/');
    return response.data;
  },

  async getPOSPChannel(id: string): Promise<{ channel_id: string | null }> {
    const { data } = await api.get(`/brokers/posps/${id}/channel/`);
    return data;
  },

  /** POSP-1: the explicit EDIT fetch. Deliberately a separate call from
   *  getPOSP, so the full account number is requested only when someone is
   *  about to change it — a profile page that merely displays the agent never
   *  pulls it over the wire. 403 for anyone but the agent. */
  async getPOSPBankDetails(id: string): Promise<POSPBankDetails> {
    const { data } = await api.get<POSPBankDetails>(`/brokers/posps/${id}/bank-details/`);
    return data;
  },

  /** Returns the STORED record (including the fresh mask), not the payload —
   *  the server normalises the IFSC to upper case and rejects a malformed one,
   *  so the caller must repaint from this answer. */
  async updatePOSPBankDetails(id: string, data: POSPBankDetailsUpdate): Promise<POSPBankDetails> {
    const response = await api.patch<POSPBankDetails>(`/brokers/posps/${id}/bank-details/`, data);
    return response.data;
  },

  /** P1 — the agent's compliance & training record.
   *
   *  Read by BOTH sides: the Broker Admin's Training & Certification card and
   *  the agent's own read-only Certifications card. The server self-scopes (a
   *  POSP caller can only resolve their own row) so there is one call, not two.
   */
  async getPOSPCompliance(id: string): Promise<POSPCompliance> {
    const { data } = await api.get<POSPCompliance>(`/brokers/posps/${id}/compliance/`);
    return data;
  },

  /** Step the lifecycle and/or record the facts. Broker staff only — an agent
   *  gets a 403, by design, and the read above is all they are offered.
   *
   *  Returns the STORED record plus `changed_fields`, so the caller repaints
   *  from the server's answer and can say what was actually written rather
   *  than claiming a save the server may have refused half of. */
  async advancePOSPCompliance(
    id: string,
    payload: POSPComplianceUpdate,
  ): Promise<POSPComplianceUpdateResponse> {
    const { data } = await api.post<POSPComplianceUpdateResponse>(
      `/brokers/posps/${id}/compliance/advance/`,
      payload,
    );
    return data;
  },

  async createPOSP(data: POSPCreateRequest): Promise<POSP> {
    const response = await api.post<POSP>('/brokers/posps/', data);
    return response.data;
  },

  async getPOSPPolicies(
    pospId: string,
    page: number = 1,
    filters?: POSPPolicyFilters,
  ): Promise<PaginatedResponse<POSPPolicy>> {
    const params = new URLSearchParams();
    params.append('page', String(page));
    if (filters?.period) params.append('period', filters.period);
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    const response = await api.get<PaginatedResponse<POSPPolicy>>(
      `/brokers/posps/${pospId}/policies/?${params.toString()}`,
    );
    return response.data;
  },

  async getPOSPEarningsSummary(
    pospId: string,
    filters?: POSPPolicyFilters,
  ): Promise<POSPEarningsSummary> {
    const params = new URLSearchParams();
    if (filters?.period) params.append('period', filters.period);
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    const response = await api.get<POSPEarningsSummary>(
      `/brokers/posps/${pospId}/earnings_summary/?${params.toString()}`,
    );
    return response.data;
  },

  /** DEAD — DO NOT CALL. `/brokers/posps/{id}/targets/` DOES NOT EXIST.
   *
   *  `POSPViewSet` declares no `targets` action and `brokers/urls.py` registers
   *  only the flat legacy `/brokers/targets/`, so every call here 404s. Its one
   *  consumer -- the Performance Targets card on the agent's own Reports page
   *  -- read that failure as "No targets set." and said so to agents whose
   *  broker admin HAD set them a number (P3).
   *
   *  The replacement is `targetService.getAchievement()` ->
   *  `GET /targets/achievement/`, which already permits posp/super_posp,
   *  already self-scopes to the caller's own POSP row, and returns the target
   *  AND the achievement in one call.
   *
   *  Left in place ONLY because `usePOSPTargets` (hooks/useBrokers.ts) still
   *  imports it and that file is outside this lane's owned paths; the pair
   *  should be deleted together. Nothing calls either one now.
   */
  async getPOSPTargets(pospId: string): Promise<POSPTarget[]> {
    const response = await api.get<POSPTarget[]>(`/brokers/posps/${pospId}/targets/`);
    return response.data;
  },

  /** An agent's payouts — the ones that actually moved money.
   *
   *  This used to GET `/brokers/posps/<id>/payouts/`, a route that DOES NOT
   *  EXIST (POSPViewSet declares no such action), so every call 404d. Both
   *  pages that use it read the failure as "no data": the agent's own portal
   *  rendered "Payouts: 0" and an empty table to somebody who had been paid.
   *
   *  The flat `/brokers/payouts/` route that does exist serves
   *  `brokers.POSPPayout` — a table NOTHING WRITES (`POSPPayout.objects.create`
   *  appears nowhere in the codebase), so it would answer empty too. The
   *  payouts that move an agent's money are `commissions.CommissionPayout`,
   *  written by the payout flow in commissions/services.py and served here.
   *
   *  Mapped to the shape the pages already render, so neither has to change. */
  async getPOSPPayouts(pospId: string): Promise<POSPPayout[]> {
    const response = await api.get<{ results: Record<string, unknown>[] }>(
      `/commissions/payouts/?posp=${pospId}&page_size=100`
    );
    return (response.data?.results ?? []).map((row) => {
      const period = String(row.period_end ?? row.period_start ?? '');
      const [year, month] = period ? period.split('-') : ['', ''];
      return {
        ...row,
        // `period_start`/`period_end` are dates; the table prints "month year".
        month: month ? MONTH_NAMES[Number(month) - 1] ?? month : '',
        year,
        total_commission: row.gross_amount,
        deductions: String(
          Number(row.tds_amount ?? 0) + Number(row.gst_amount ?? 0)
        ),
        net_payout: row.net_amount,
      } as unknown as POSPPayout;
    });
  },

  async createPOSPLogin(id: string): Promise<POSPCreateLoginResponse> {
    const response = await api.post<POSPCreateLoginResponse>(`/brokers/posps/${id}/create-login/`);
    return response.data;
  },

  async activatePOSP(id: string): Promise<{ status: string }> {
    const response = await api.post<{ status: string }>(`/brokers/posps/${id}/activate/`);
    return response.data;
  },

  async suspendPOSP(id: string, reason?: string): Promise<{ status: string }> {
    const response = await api.post<{ status: string }>(`/brokers/posps/${id}/suspend/`, { reason });
    return response.data;
  },

  async terminatePOSP(id: string, reason?: string): Promise<{ status: string }> {
    const response = await api.post<{ status: string }>(`/brokers/posps/${id}/terminate/`, { reason });
    return response.data;
  },

  async getBrokerSettings(brokerId: string) {
    const response = await api.get(`/brokers/${brokerId}/settings/`);
    return response.data;
  },

  async updateBrokerSettings(brokerId: string, data: Record<string, unknown>) {
    const response = await api.patch(`/brokers/${brokerId}/settings/`, data);
    return response.data;
  },

  async submitPOSPDocuments(id: string, formData: FormData): Promise<POSP> {
    const response = await api.post<POSP>(`/brokers/posps/${id}/submit-documents/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  async approvePOSP(id: string): Promise<POSP> {
    const response = await api.post<POSP>(`/brokers/posps/${id}/approve/`);
    return response.data;
  },

  async rejectPOSP(id: string, reason: string): Promise<POSP> {
    const response = await api.post<POSP>(`/brokers/posps/${id}/reject/`, { reason });
    return response.data;
  },

  async resubmitPOSPDocuments(id: string, formData: FormData): Promise<POSP> {
    const response = await api.post<POSP>(`/brokers/posps/${id}/resubmit/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};
